// src/components/WalletPage.js
// Wallet: what can be sent now, loading by Interac e-Transfer, loads on their
// way, and the full history of wallet movements (TD-15 ledger).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";
import { getWalletBalance, getWalletLoads, loadWallet, getWalletHistory } from "../services/api";

const PENDING = ["REQUESTED", "INITIATED", "PENDING_DEPOSIT", "RECEIVED", "REVIEW_REQUIRED"];
const LOAD_STATUS = {
  REQUESTED: ["Sending Interac request", "blue"],
  INITIATED: ["Sending Interac request", "blue"],
  PENDING_DEPOSIT: ["Waiting for you to accept", "yellow"],
  RECEIVED: ["Money received, adding to wallet", "blue"],
  REVIEW_REQUIRED: ["Being checked", "yellow"],
  CREDITED: ["Added to your wallet", "green"],
  FAILED: ["Could not send", "red"],
  DECLINED: ["Declined", "red"],
  EXPIRED: ["Expired", "gray"],
  RETURNED: ["Returned to your bank", "gray"],
};
const BADGE = {
  blue: "bg-blue-100 text-blue-800",
  yellow: "bg-yellow-100 text-yellow-800",
  green: "bg-green-100 text-green-800",
  red: "bg-red-100 text-red-800",
  gray: "bg-gray-100 text-gray-700",
};
const QUICK = [50, 100, 250, 500, 1000];

const money = (n) => Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const when = (d) => (d ? new Date(d).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "");
const newKey = () => "load-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);

/** Same rule as the server: a plain positive number, at most 2 decimals. */
function checkAmount(raw, limits) {
  const t = String(raw).trim();
  if (!t) return "Enter an amount";
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return "Enter a plain amount with up to 2 decimals (for example 150 or 150.50)";
  const n = Number(t);
  if (!(n > 0)) return "Enter an amount above zero";
  if (!limits) return null;
  if (n < limits.min) return `The minimum load is CAD ${money(limits.min)}`;
  if (n > limits.max) return `The maximum per load is CAD ${money(limits.max)}`;
  if (n > limits.remaining + 1e-9) {
    return `Your wallet can hold up to CAD ${money(limits.walletCap)} including loads on their way. You can load up to CAD ${money(limits.remaining)} right now.`;
  }
  return null;
}

export default function WalletPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [info, setInfo] = useState(null);         // { loads, limits, verification, enabled, instant }
  const [history, setHistory] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [result, setResult] = useState(null);     // last load response
  const keyRef = useRef(null);                     // reused on retry of the same amount

  const refresh = useCallback(async () => {
    try {
      const [b, l, h] = await Promise.all([getWalletBalance(), getWalletLoads(10), getWalletHistory(null, 25)]);
      setBalance(b); setInfo(l); setHistory(h.transactions || []); setNextBefore(h.nextBefore || null);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || "Could not load your wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // While a load is on its way, check again every 15 seconds (stops after 20 minutes).
  const anyPending = Boolean(info && (info.loads || []).some((x) => PENDING.includes(x.status)));
  useEffect(() => {
    if (!anyPending) return undefined;
    const started = Date.now();
    const id = setInterval(() => {
      if (Date.now() - started > 20 * 60 * 1000) { clearInterval(id); return; }
      refresh();
    }, 15000);
    return () => clearInterval(id);
  }, [anyPending, refresh]);

  async function moreHistory() {
    try {
      const h = await getWalletHistory(nextBefore, 25);
      setHistory((prev) => prev.concat(h.transactions || []));
      setNextBefore(h.nextBefore || null);
    } catch (e) {
      setLoadError(e.message);
    }
  }

  function changeAmount(v) {
    setAmount(v);
    setFormError(null);
    keyRef.current = null; // a different amount is a different request
  }

  async function submit(e) {
    e.preventDefault();
    const problem = checkAmount(amount, info && info.limits);
    if (problem) { setFormError(problem); return; }
    setSubmitting(true); setFormError(null); setResult(null);
    if (!keyRef.current) keyRef.current = newKey();
    try {
      const r = await loadWallet(Number(amount).toFixed(2), keyRef.current);
      setResult(r);
      keyRef.current = null;
      setAmount("");
      await refresh();
    } catch (err) {
      // Keep the key: trying again with the same amount can never create a second request.
      setFormError(err.message || "The load could not be started");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (<><Navigation /><p className="p-6">Loading your wallet…</p></>);
  }

  const cur = "CAD";
  const available = balance && balance.available ? Number(balance.available[cur] || 0) : 0;
  const held = balance && balance.held ? Number(balance.held[cur] || 0) : 0;
  const pending = balance && balance.pendingLoads ? Number(balance.pendingLoads[cur] || 0) : 0;
  const limits = info && info.limits;
  const ver = (info && info.verification) || {};
  const verified = ver.idVerified && ver.phoneVerified;
  const quick = limits ? QUICK.filter((q) => q >= limits.min && q <= limits.maxNow) : [];

  return (
    <>
      <Navigation />
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold">Wallet</h1>
        {loadError && <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-sm">{loadError}</div>}

        {/* Balance summary */}
        <div className="border p-4 rounded shadow">
          <p className="text-sm text-gray-600">Available to send</p>
          <p className="text-3xl font-bold">{cur} {money(available)}</p>
          <div className="mt-3 text-sm text-gray-700 space-y-1">
            {held > 0 && (
              <p>Held for orders in progress: <span className="font-semibold">{cur} {money(held)}</span>
                <span className="text-gray-500"> — taken once delivery is confirmed, or returned if it is not</span></p>
            )}
            {pending > 0 && (
              <p>Loads on their way: <span className="font-semibold">{cur} {money(pending)}</span>
                <span className="text-gray-500"> — added when the money arrives</span></p>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
              disabled={!(available > 0) || !verified} onClick={() => navigate("/create-order")}>Send money</button>
            <button className="border px-4 py-2 rounded disabled:opacity-50"
              disabled={!(available > 0) || !verified} onClick={() => navigate("/airtime")}>Mobile top-up</button>
          </div>
        </div>

        {/* Load funds */}
        <div className="border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-1">Load your wallet</h2>
          <p className="text-sm text-gray-600 mb-3">By Interac e-Transfer from your Canadian bank.</p>

          {info && !info.enabled ? (
            <p className="text-sm text-gray-700">Loading your wallet is not available right now. Please try again later.</p>
          ) : !verified ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 p-3 rounded text-sm">
              <p className="font-semibold mb-2">Verify your account to load your wallet and send money.</p>
              <div className="flex flex-wrap gap-2">
                {!ver.idVerified && (
                  <button className="bg-yellow-600 text-white px-3 py-1 rounded" onClick={() => navigate("/kyc")}>Verify my ID</button>
                )}
                {!ver.phoneVerified && (
                  <button className="bg-yellow-600 text-white px-3 py-1 rounded" onClick={() => navigate("/verify-phone")}>Verify my phone</button>
                )}
              </div>
            </div>
          ) : limits && limits.maxNow < limits.min ? (
            <p className="text-sm text-gray-700">
              Your wallet is at its limit of {cur} {money(limits.walletCap)}, including loads on their way.
              Send money first, then you can load more.
            </p>
          ) : (
            <form onSubmit={submit} noValidate>
              <label className="block text-sm font-medium mb-1" htmlFor="load-amount">Amount (CAD)</label>
              <input id="load-amount" inputMode="decimal" autoComplete="off"
                className="border rounded p-2 w-full text-lg" placeholder={limits ? `${limits.min} to ${limits.maxNow}` : ""}
                value={amount} onChange={(e) => changeAmount(e.target.value)} />
              {quick.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {quick.map((q) => (
                    <button type="button" key={q} className="border rounded px-3 py-1 text-sm hover:bg-gray-50"
                      onClick={() => changeAmount(String(q))}>{cur} {q}</button>
                  ))}
                </div>
              )}
              {limits && (
                <p className="text-xs text-gray-500 mt-2">
                  {cur} {money(limits.min)} to {cur} {money(limits.max)} per load · you can load up to {cur} {money(limits.maxNow)} right now
                  (wallet limit {cur} {money(limits.walletCap)}).
                </p>
              )}
              {formError && <p className="text-sm text-red-700 mt-2">{formError}</p>}
              <button type="submit" disabled={submitting}
                className="bg-green-600 text-white p-2 w-full rounded mt-3 disabled:opacity-50">
                {submitting ? "Sending request…" : info && info.instant ? "Load wallet" : "Request Interac e-Transfer"}
              </button>
            </form>
          )}

          {result && result.funding && (
            ["FAILED", "DECLINED", "EXPIRED", "RETURNED"].includes(result.funding.status) ? (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-sm mt-3">
                {result.funding.failureReason || "The Interac request could not be sent. Nothing was taken from your bank."}
                {" "}Please try again in a few minutes.
              </div>
            ) : result.credited ? (
              <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded text-sm mt-3">
                {cur} {money(result.funding.amount)} has been added to your wallet.
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded text-sm mt-3">
                <p className="font-semibold mb-1">Request sent for {cur} {money(result.funding.amount)}</p>
                <p>{result.funding.instructions || "Check your email for the Interac e-Transfer request and accept it from your bank."}</p>
                <p className="mt-1 text-blue-800">Your wallet is credited as soon as the money arrives. This page updates by itself.</p>
              </div>
            )
          )}
        </div>

        {/* Recent loads */}
        {info && (info.loads || []).length > 0 && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-bold text-lg mb-3">Recent loads</h2>
            <ul className="divide-y">
              {info.loads.map((l) => {
                const [label, tone] = LOAD_STATUS[l.status] || [l.status, "gray"];
                return (
                  <li key={l.id} className="py-2 text-sm">
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-semibold">{l.currency} {money(l.amount)}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${BADGE[tone]}`}>{label}</span>
                    </div>
                    <div className="text-gray-500">{when(l.createdAt)}</div>
                    {l.failureReason && <div className="text-gray-700 mt-1">{l.failureReason}</div>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* History */}
        <div className="border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-3">Wallet history</h2>
          {history.length === 0 ? (
            <p className="text-gray-500 text-sm">No wallet activity yet.</p>
          ) : (
            <ul className="divide-y">
              {history.map((t) => {
                const bal = Number(t.balanceChange);
                const heldOnly = bal === 0;
                return (
                  <li key={t.id} className="py-2 text-sm flex justify-between gap-3">
                    <div>
                      <div className="font-medium">
                        {t.label}{t.orderReference ? <span className="font-mono text-gray-600"> {t.orderReference}</span> : null}
                      </div>
                      <div className="text-gray-500">{when(t.createdAt)}</div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      {heldOnly ? (
                        <span className="text-gray-600">
                          {Number(t.heldChange) > 0 ? "Reserved" : "Released"} {t.currency} {money(t.amount)}
                        </span>
                      ) : (
                        <span className={bal > 0 ? "text-green-700 font-semibold" : "text-gray-900 font-semibold"}>
                          {bal > 0 ? "+" : "−"}{t.currency} {money(Math.abs(bal))}
                        </span>
                      )}
                      <div className="text-xs text-gray-500">Available after: {t.currency} {money(t.availableAfter)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {nextBefore && (
            <button className="border rounded px-3 py-1 text-sm mt-3" onClick={moreHistory}>Show older</button>
          )}
        </div>
      </div>
    </>
  );
}
